import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Loader2, MessageSquare, Printer, Upload } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  API,
  archiveDocument,
  getDocument,
  listDocumentTypes,
  restoreDocument,
  sendDocumentMessage,
  updateDocument,
  uploadDocumentVersion,
  uploadSpec,
  createDocument,
} from "../lib/api";
import { WORKSPACE } from "../constants/testIds";

export default function DocumentDetail() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const isAdmin = !!localStorage.getItem("demaxtore_admin_token");
  const [types, setTypes] = useState([]);

  const [name, setName] = useState("");
  const [documentType, setDocumentType] = useState("Other");
  const [notes, setNotes] = useState("");
  const [versionChanges, setVersionChanges] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const data = await getDocument(documentId);
      setDoc(data);
      setName(data.name || "");
      setDocumentType(data.document_type || "Other");
      setNotes(data.notes || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load document");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    listDocumentTypes()
      .then((r) => setTypes(r.types || []))
      .catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  const sendMsg = async () => {
    if (!msg.trim()) return;
    try {
      await sendDocumentMessage(documentId, msg.trim());
      toast.success("Message sent");
      setMsg("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message");
    }
  };

  const downloadUrl = `${API}/documents/${documentId}/download`;
  const previewUrl = doc?.file_id ? `${API}/rfq/attachment/${doc.file_id}` : null;
  const mime = (doc?.mime_type || "").toLowerCase();
  const isPdf = mime.includes("pdf") || (doc?.filename || "").toLowerCase().endsWith(".pdf");
  const isImage =
    mime.startsWith("image/") ||
    /\.(jpe?g|png)$/i.test(doc?.filename || "");
  const isOffice =
    mime.includes("word") ||
    mime.includes("officedocument") ||
    /\.docx?$/i.test(doc?.filename || "");

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-[hsl(217,90%,25%)]" />
      </div>
    );
  }
  if (!doc) {
    return (
      <div className="min-h-screen grid place-items-center text-neutral-500">
        Document not found.
      </div>
    );
  }

  const versions = [...(doc.versions || [])].sort((a, b) => (b.version || 0) - (a.version || 0));
  const rel = doc.relationships || {};

  return (
    <div className="min-h-screen bg-[hsl(60,5%,98%)]" data-testid={WORKSPACE.documentsDetail}>
      <header className="border-b border-neutral-200 bg-white sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/workspace/documents" className="inline-flex items-center gap-3">
            <span className="w-8 h-8 rounded-sm bg-[hsl(217,90%,25%)] grid place-items-center">
              <span className="text-white font-bold text-sm">D</span>
            </span>
            <span className="font-medium tracking-tight text-lg">DeMaxtore</span>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-400 ml-4 hidden sm:inline">
              Document
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-sm h-10" asChild>
              <a href={downloadUrl}>
                <Download className="w-4 h-4 mr-2" /> Download
              </a>
            </Button>
            <Button
              variant="outline"
              className="rounded-sm h-10"
              onClick={() => window.print()}
            >
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10 space-y-10">
        <button
          type="button"
          onClick={() => navigate("/workspace/documents")}
          className="inline-flex items-center text-sm text-neutral-600 hover:text-neutral-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> All documents
        </button>

        {/* Section 1 — Summary */}
        <section className="border border-neutral-200 bg-white rounded-sm p-6 lg:p-8">
          <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[hsl(217,90%,35%)]">
            Document Summary
          </div>
          <h1 className="mt-3 text-3xl font-light tracking-tight text-[hsl(217,90%,25%)]">
            {doc.name}
          </h1>
          <div className="mt-2 font-mono text-sm text-neutral-500">{doc.number}</div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Badge className="rounded-sm bg-[hsl(217,90%,25%)] text-white uppercase text-[10px] tracking-wider">
              {doc.document_type}
            </Badge>
            <Badge
              className={`rounded-sm uppercase text-[10px] tracking-wider text-white ${
                doc.status === "Archived" ? "bg-neutral-500" : "bg-emerald-700"
              }`}
            >
              {doc.status}
            </Badge>
            <Badge variant="outline" className="rounded-sm uppercase text-[10px] tracking-wider">
              v{doc.version}
            </Badge>
          </div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <Meta
              label="Purchase Order"
              value={
                doc.po_id ? (
                  <Link
                    to={`/workspace/purchase-orders/${doc.po_id}`}
                    className="text-[hsl(217,90%,25%)] hover:underline font-mono"
                  >
                    {doc.po_number}
                  </Link>
                ) : (
                  "—"
                )
              }
            />
            <Meta
              label="Shipment"
              value={
                doc.tracking_id ? (
                  <Link
                    to={`/workspace/shipments/${doc.tracking_id}`}
                    className="text-[hsl(217,90%,25%)] hover:underline font-mono"
                  >
                    {doc.tracking_number}
                  </Link>
                ) : (
                  "—"
                )
              }
            />
            <Meta
              label="Inspection"
              value={
                doc.inspection_id ? (
                  <Link
                    to={`/workspace/inspections/${doc.inspection_id}`}
                    className="text-[hsl(217,90%,25%)] hover:underline font-mono"
                  >
                    {doc.inspection_number}
                  </Link>
                ) : (
                  "—"
                )
              }
            />
            <Meta
              label="FreightIQ"
              value={
                doc.freightiq_id ? (
                  <Link
                    to={`/workspace/freightiq/${doc.freightiq_id}`}
                    className="text-[hsl(217,90%,25%)] hover:underline font-mono"
                  >
                    {doc.freightiq_number}
                  </Link>
                ) : (
                  "—"
                )
              }
            />
            <Meta label="Buyer" value={doc.buyer?.company || doc.buyer?.name || "—"} />
            <Meta label="Manufacturer" value={doc.manufacturer?.name || "—"} />
            <Meta label="Uploaded By" value={doc.uploaded_by || "—"} />
            <Meta
              label="Created"
              value={doc.created_at ? new Date(doc.created_at).toLocaleString() : "—"}
            />
          </div>
        </section>

        {/* Section 2 — Preview */}
        <section data-testid={WORKSPACE.documentsPreview}>
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
            Preview
          </h2>
          <div className="border border-neutral-200 bg-white rounded-sm p-4 min-h-[280px]">
            {isPdf && previewUrl ? (
              <iframe
                title="PDF preview"
                src={previewUrl}
                className="w-full h-[520px] border-0 rounded-sm"
              />
            ) : isImage && previewUrl ? (
              <div className="grid place-items-center p-4">
                <img
                  src={previewUrl}
                  alt={doc.name}
                  className="max-h-[480px] max-w-full object-contain"
                />
              </div>
            ) : isOffice ? (
              <div className="p-12 text-center text-sm text-neutral-500">
                Office file preview is limited in-browser. Use Download to open{" "}
                <span className="font-mono">{doc.filename}</span>.
              </div>
            ) : (
              <div className="p-12 text-center text-sm text-neutral-500">
                Preview not available for this file type.{" "}
                <a href={downloadUrl} className="text-[hsl(217,90%,25%)] hover:underline">
                  Download
                </a>
              </div>
            )}
          </div>
        </section>

        {/* Section 3 — Versions */}
        <section data-testid={WORKSPACE.documentsVersions}>
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
            Versions
          </h2>
          <div className="border border-neutral-200 bg-white rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(60,5%,96%)] text-left">
                <tr>
                  {["Version", "Uploaded By", "Date", "Changes", "Current"].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-neutral-500"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {versions.map((v) => (
                  <tr key={v.version} className="border-t border-neutral-100">
                    <td className="px-4 py-3 font-mono">v{v.version}</td>
                    <td className="px-4 py-3">{v.uploaded_by || "—"}</td>
                    <td className="px-4 py-3 text-neutral-600">
                      {v.uploaded_at ? new Date(v.uploaded_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-neutral-500">{v.changes || "—"}</td>
                    <td className="px-4 py-3">
                      {v.current ? (
                        <span className="text-emerald-700 text-xs uppercase tracking-wider font-semibold">
                          Current
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 4 — Relationships */}
        <section>
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
            Relationships
          </h2>
          <div className="border border-neutral-200 bg-white rounded-sm p-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <Meta
              label="Linked Purchase Order"
              value={
                rel.purchase_order?.id ? (
                  <Link
                    to={`/workspace/purchase-orders/${rel.purchase_order.id}`}
                    className="text-[hsl(217,90%,25%)] hover:underline font-mono"
                  >
                    {rel.purchase_order.number}
                  </Link>
                ) : (
                  "—"
                )
              }
            />
            <Meta
              label="Linked Shipment"
              value={
                rel.shipment?.id ? (
                  <Link
                    to={`/workspace/shipments/${rel.shipment.id}`}
                    className="text-[hsl(217,90%,25%)] hover:underline font-mono"
                  >
                    {rel.shipment.number}
                  </Link>
                ) : (
                  "—"
                )
              }
            />
            <Meta
              label="Linked Inspection"
              value={
                rel.inspection?.id ? (
                  <Link
                    to={`/workspace/inspections/${rel.inspection.id}`}
                    className="text-[hsl(217,90%,25%)] hover:underline font-mono"
                  >
                    {rel.inspection.number}
                  </Link>
                ) : (
                  "—"
                )
              }
            />
            <Meta
              label="Linked FreightIQ"
              value={
                rel.freightiq?.id ? (
                  <Link
                    to={`/workspace/freightiq/${rel.freightiq.id}`}
                    className="text-[hsl(217,90%,25%)] hover:underline font-mono"
                  >
                    {rel.freightiq.number}
                  </Link>
                ) : (
                  "—"
                )
              }
            />
            <Meta
              label="Linked RFQ"
              value={rel.rfq?.id ? rel.rfq.number || rel.rfq.id : "—"}
            />
          </div>
        </section>

        {/* Section 5 — Timeline */}
        <section data-testid={WORKSPACE.documentsTimeline}>
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
            Timeline
          </h2>
          <ol className="border border-neutral-200 bg-white rounded-sm divide-y divide-neutral-100">
            {(doc.timeline || []).map((t) => (
              <li key={t.key} className="p-4 flex items-center gap-3 text-sm">
                <span
                  className={`w-2.5 h-2.5 rounded-full ${
                    t.status === "done" ? "bg-emerald-600" : "bg-neutral-300"
                  }`}
                />
                <span className="font-medium flex-1">{t.label}</span>
                <span className="text-neutral-400 text-xs">
                  {t.at ? new Date(t.at).toLocaleString() : "Pending"}
                </span>
              </li>
            ))}
          </ol>
        </section>

        {/* Message */}
        <section>
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
            Send Message
          </h2>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              placeholder="Message operations about this document…"
              className="h-11 rounded-sm flex-1"
            />
            <Button variant="outline" className="rounded-sm h-11" onClick={sendMsg}>
              <MessageSquare className="w-4 h-4 mr-2" /> Send
            </Button>
          </div>
        </section>

        {/* Ops */}
        {isAdmin && (
          <section
            data-testid={WORKSPACE.documentsOpsPanel}
            className="border border-[hsl(217,90%,25%)]/20 bg-white rounded-sm p-6 space-y-6"
          >
            <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-[hsl(217,90%,25%)]">
              Operations
            </h2>

            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Document name
                </span>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Category
                </span>
                <select
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                  className="mt-2 h-11 w-full rounded-sm border border-neutral-200 px-3 text-sm bg-white"
                >
                  {(types.length ? types : [documentType]).map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm sm:col-span-2">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Notes
                </span>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="mt-2 min-h-[70px] rounded-sm"
                />
              </label>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                disabled={busy}
                variant="outline"
                className="rounded-sm h-10"
                onClick={async () => {
                  setBusy(true);
                  try {
                    await updateDocument(documentId, {
                      name: name || doc.name,
                      document_type: documentType,
                      notes: notes || null,
                    });
                    toast.success("Metadata saved");
                    await load();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Update failed");
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Save metadata
              </Button>

              {doc.status !== "Archived" ? (
                <Button
                  disabled={busy}
                  variant="outline"
                  className="rounded-sm h-10"
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await archiveDocument(documentId);
                      toast.success("Document archived");
                      await load();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Archive failed");
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  Archive
                </Button>
              ) : (
                <Button
                  disabled={busy}
                  className="rounded-sm h-10 bg-emerald-700 text-white"
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await restoreDocument(documentId);
                      toast.success("Document restored");
                      await load();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Restore failed");
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  Restore
                </Button>
              )}
            </div>

            <div className="border-t border-neutral-100 pt-4 space-y-3">
              <div className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                Replace version
              </div>
              <Input
                value={versionChanges}
                onChange={(e) => setVersionChanges(e.target.value)}
                placeholder="Describe changes…"
                className="h-10 rounded-sm"
              />
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer border border-neutral-200 rounded-sm px-3 h-10 hover:bg-neutral-50">
                <Upload className="w-4 h-4" /> Upload new version
                <input
                  type="file"
                  accept=".pdf,.docx,.jpg,.jpeg,.png"
                  className="hidden"
                  disabled={busy}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setBusy(true);
                    try {
                      const uploaded = await uploadSpec(file);
                      await uploadDocumentVersion(documentId, {
                        file: {
                          file_id: uploaded.file_id,
                          filename: uploaded.filename,
                          content_type: uploaded.content_type,
                          size: uploaded.size,
                          stored_path: uploaded.stored_path,
                        },
                        changes: versionChanges || `Replaced with ${uploaded.filename}`,
                      });
                      toast.success("New version uploaded");
                      setVersionChanges("");
                      await load();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Version upload failed");
                    } finally {
                      setBusy(false);
                      e.target.value = "";
                    }
                  }}
                />
              </label>
            </div>

            <OpsUploadRelated poId={doc.po_id} types={types} onDone={load} />
          </section>
        )}
      </main>
    </div>
  );
}

function OpsUploadRelated({ poId, types, onDone }) {
  const [busy, setBusy] = useState(false);
  const [docType, setDocType] = useState("Other");
  const [docName, setDocName] = useState("");

  return (
    <div className="border-t border-neutral-100 pt-4 space-y-3">
      <div className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
        Upload related document
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Input
          value={docName}
          onChange={(e) => setDocName(e.target.value)}
          placeholder="Document name"
          className="h-10 rounded-sm"
        />
        <select
          value={docType}
          onChange={(e) => setDocType(e.target.value)}
          className="h-10 rounded-sm border border-neutral-200 px-3 text-sm bg-white"
        >
          {(types.length ? types : ["Other"]).map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>
      <label className="inline-flex items-center gap-2 text-sm cursor-pointer border border-neutral-200 rounded-sm px-3 h-10 hover:bg-neutral-50">
        <Upload className="w-4 h-4" /> Upload document
        <input
          type="file"
          accept=".pdf,.docx,.jpg,.jpeg,.png"
          className="hidden"
          disabled={busy}
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            setBusy(true);
            try {
              const uploaded = await uploadSpec(file);
              await createDocument({
                name: docName || uploaded.filename,
                document_type: docType,
                file: {
                  file_id: uploaded.file_id,
                  filename: uploaded.filename,
                  content_type: uploaded.content_type,
                  size: uploaded.size,
                  stored_path: uploaded.stored_path,
                },
                po_id: poId || undefined,
              });
              toast.success("Document uploaded to hub");
              setDocName("");
              if (onDone) await onDone();
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Upload failed");
            } finally {
              setBusy(false);
              e.target.value = "";
            }
          }}
        />
      </label>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-400 font-semibold">
        {label}
      </div>
      <div className="mt-1 text-neutral-800 break-words">{value ?? "—"}</div>
    </div>
  );
}
